import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TailSpin } from 'react-loader-spinner';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { getTestProps } from '../../lib/helpers';
import { emailSchema, passwordSchema } from '../../lib/zodHelpers';

import Button from '../../components/Button/Button';

import { WarningIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

const RegisterForm = ({ onSubmit, prefilledEmail = '', testId }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  const validationSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(1, t('Form.FormErrorNotBlank')),
        lastName: z.string().min(1, t('Form.FormErrorNotBlank')),
        email: emailSchema(t),
        plainPassword: passwordSchema(t),
      }),
    [t],
  );

  const initialValues = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      email: prefilledEmail ?? '',
      plainPassword: '',
    }),
    [prefilledEmail],
  );

  const onFormSubmit = useCallback(
    async ({ value, formApi }) => {
      if (!onSubmit) return;

      setIsLoading(true);
      const errors = await onSubmit({
        ...value,
        privacyPolicy: true,
        termsOfService: true,
      });

      if (errors && Object.keys(errors).length > 0) {
        Object.entries(errors).forEach(([key, error]) => {
          if (key === 'general') {
            setGeneralError(error);
          } else if (
            ['firstName', 'lastName', 'email', 'plainPassword'].includes(key)
          ) {
            formApi.setFieldMeta(key, (prev) => ({
              ...prev,
              errorMap: { onSubmit: error },
            }));
          }
        });
      }
      setIsLoading(false);
    },
    [onSubmit],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: onFormSubmit,
    validators: {
      onSubmit: validationSchema,
    },
  });

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id="register-form"
        className="w-full"
        onSubmit={handleSubmit}
        noValidate={true}
        {...getTestProps(testId)}
      >
        <div className="grid grid-cols-2 gap-4 2xl:gap-6 pb-6">
          <form.AppField name="firstName">
            {(field) => (
              <field.Input
                placeholder={t('Global.FirstName')}
                {...getTestProps(testId, 'firstName', 'testId')}
              />
            )}
          </form.AppField>
          <form.AppField name="lastName">
            {(field) => (
              <field.Input
                placeholder={t('Global.LastName')}
                {...getTestProps(testId, 'lastName', 'testId')}
              />
            )}
          </form.AppField>
        </div>
        <div className="flex flex-wrap gap-4 2xl:gap-6 ">
          <form.AppField name="email">
            {(field) => (
              <field.Input
                placeholder={t('Global.Email')}
                additionalInputClasses={
                  prefilledEmail ? 'cursor-not-allowed' : ''
                }
                disabled={!!prefilledEmail}
                {...getTestProps(testId, 'email', 'testId')}
              />
            )}
          </form.AppField>
          <form.AppField name="plainPassword">
            {(field) => (
              <field.Input
                type="password"
                placeholder={t('Global.Password')}
                showPasswordInfo
                {...getTestProps(testId, 'password', 'testId')}
              />
            )}
          </form.AppField>
        </div>
        <div className="dark:text-white my-8 lg:my-5 2xl:my-8 text-sm md:text-base opacity-70 text-center">
          <Trans
            i18nKey="Global.DataProcessing"
            components={{
              1: (
                <Link
                  className="text-blue underline"
                  to="https://flotiq.com/terms-of-service/"
                  target="_blank"
                  rel="noreferrer"
                />
              ),
              2: (
                <Link
                  className="text-blue underline"
                  to="https://flotiq.com/privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </div>
        <div className="w-full flex flex-col justify-center text-center">
          <Button
            id="button_register_with_email"
            buttonSize="lg"
            type="submit"
            additionalClasses="md:min-w-[300px] justify-center"
            disabled={isLoading}
            {...getTestProps(testId, 'submit', 'testId')}
          >
            {t('Global.CreateAnAccount')}
            <TailSpin
              height="15"
              width="20"
              color="white"
              ariaLabel="tail-spin-loading"
              radius="1"
              wrapperClass="ml-4"
              visible={isLoading}
            />
          </Button>
          {generalError && (
            <div className="text-red py-2 inline-flex items-center">
              <WarningIcon className="h-5 w-5 mr-2" />
              <div className="block text-left">{generalError}</div>
            </div>
          )}
        </div>
      </form>
    </form.AppForm>
  );
};

export default RegisterForm;

RegisterForm.propTypes = {
  /**
   * On submit handler
   */
  onSubmit: PropTypes.func.isRequired,
  /**
   * Default email form value
   */
  prefilledEmail: PropTypes.string,
  /**
   * Form test id
   */
  testId: PropTypes.string,
};
